import { HttpStatusCodes } from '@service-initiation/shared-clients-http';
import { LoggerWarning } from '@service-initiation/shared-clients-logger';
import { logger } from '@service-initiation/shared-clients-osp-logger';
import {
  type LocateStoresRequest,
  type Store,
} from '@service-initiation/shared-clients-tech-care-services';
import { toIsoDate } from '@service-initiation/shared-utils-date-time';
import { queryClient } from '@service-initiation/shared-utils-query-client';
import {
  validateCANPostalCode,
  validateUSZipCode,
  validateZipCode,
} from '@service-initiation/tech-care-utils-validators';

import { techCareServicesClient } from './clients/techCareServicesClient';
import { type GeoLocation } from './types';

export type LocateStoresInput = {
  calcNextAvailability: boolean;
  fullAddress?: string;
  geoLocation?: GeoLocation;
  maxDistance?: number;
  maxNumOfStoresToReturn?: number;
  postalCode?: string;
};

export const locateStores = async ({
  calcNextAvailability,
  fullAddress,
  geoLocation,
  maxDistance,
  maxNumOfStoresToReturn,
  postalCode,
}: LocateStoresInput): Promise<Store[]> => {
  if (postalCode && !validateZipCode(postalCode)) {
    logger.warn(
      new LoggerWarning(
        'Failed to locate stores',
        'corrupted postalCode when fetching available stores'
      ),
      {
        postalCode,
      }
    );
    return [];
  }

  return await fetchAvailableStores({
    calcNextAvailability,
    fullAddress,
    geoLocation,
    maxDistance,
    maxNumOfStoresToReturn,
    postalCode,
  });
};

const fetchAvailableStores = async ({
  calcNextAvailability,
  fullAddress,
  geoLocation,
  maxDistance = 60,
  maxNumOfStoresToReturn = 10,
  postalCode,
}: {
  calcNextAvailability: boolean;
  fullAddress?: string;
  geoLocation?: GeoLocation;
  maxDistance?: number;
  maxNumOfStoresToReturn?: number;
  postalCode?: string;
}): Promise<Store[]> => {
  const isoDate = toIsoDate(new Date());

  const validatedPostalCode =
    (postalCode &&
      ((validateUSZipCode(postalCode) && `US,${postalCode}`) ||
        (validateCANPostalCode(postalCode) && `CAN,${postalCode}`))) ||
    undefined;
  const formattedAddress =
    validatedPostalCode ||
    (geoLocation && `${geoLocation.latitude},${geoLocation.longitude}`) ||
    (fullAddress && `${fullAddress}`);

  try {
    const request: LocateStoresRequest = {
      address: formattedAddress,
      distance_km: 'false',
      max_distance: maxDistance.toString(),
      next_available_appointment: calcNextAvailability,
      num_stores: maxNumOfStoresToReturn.toString(),
      start_date: isoDate,
    };
    return await queryClient.fetchQuery({
      queryFn: async () => {
        const response = await techCareServicesClient.locateStores(request);

        if (response.status !== HttpStatusCodes.OK) {
          throw new Error('Received invalid response status from server.');
        }

        return response.body.stores || [];
      },
      queryKey: ['locateStores', request],
      retry: 2,
      staleTime: Infinity,
    });
  } catch (e) {
    const error = e as Error;
    error.message =
      'Error occurred while trying to locate stores for the specified zip code';
    logger.error(error, {
      calcNextAvailability,
      postalCode,
    });
    throw error;
  }
};
